import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getClientPriceListDetails,
  downloadClientPriceListExport,
  updateClientPriceList,
  convertPriceListToConfirmedOrder,
  markClientPriceListSent,
  ClientPriceList,
  ClientPriceListItem,
} from '../lib/clientPriceListApi';
import { getClientPriceListPublicLink } from '../lib/clientPriceListLink';
import { FiSearch, FiArrowLeft } from 'react-icons/fi';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import ClientPriceListEmailDialog from '../components/ClientPriceListEmailDialog';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const CLIENT_PRICE_LIST_DETAIL_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>This is the detail view of a Client Price List. You can edit quantity, remarks, and internal remarks per item.</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Search and filter items</li>
      <li>Save changes after editing</li>
      <li>Send the price list via email or Export to Excel</li>
    </ul>
  </div>
);

export default function ClientPriceListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [list, setList] = useState<ClientPriceList | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedItems, setEditedItems] = useState<Array<{ quantity: number; remarks: string; internal_remarks: string }>>([]);
  // Section A
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const fetchList = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getClientPriceListDetails(id);
      setList(res.data ?? null);
    } catch (e) {
      console.error('Failed to load price list:', e);
      setList(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (list?.items?.length && editedItems.length !== list.items.length) {
      setEditedItems(
        list.items.map((it) => ({
          quantity: typeof it.quantity === 'number' && it.quantity >= 0 ? it.quantity : 0,
          remarks: it.remarks || '',
          internal_remarks: it.internal_remarks || '',
        }))
      );
    }
  }, [list?.items]);

  const handleEditChange = (idx: number, field: 'quantity' | 'remarks' | 'internal_remarks', value: number | string) => {
    setEditedItems((prev) => {
      const next = [...prev];
      if (!next[idx]) next[idx] = { quantity: 0, remarks: '', internal_remarks: '' };
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const hasEdits = useMemo(() => {
    if (!list?.items?.length || editedItems.length !== list.items.length) return false;
    return list.items.some((it, idx) => {
      const ed = editedItems[idx];
      if (!ed) return false;
      const q = typeof it.quantity === 'number' && it.quantity >= 0 ? it.quantity : 0;
      return ed.quantity !== q || (ed.remarks || '') !== (it.remarks || '') || (ed.internal_remarks || '') !== (it.internal_remarks || '');
    });
  }, [list?.items, editedItems]);

  const handleSaveEdits = async () => {
    if (!list?._id || !hasEdits) return;
    setSaving(true);
    try {
      const itemsPayload = list.items.map((it, idx) => {
        const ed = editedItems[idx] ?? { quantity: 0, remarks: '', internal_remarks: '' };
        return {
          markup: it.markup ?? undefined,
          quantity: ed.quantity,
          remarks: ed.remarks,
          internal_remarks: ed.internal_remarks,
        };
      });
      await updateClientPriceList(list._id, { items: itemsPayload });
      await fetchList();
    } catch (e) {
      console.error('Failed to save:', e);
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const filteredItemsWithIndex = useMemo((): Array<[ClientPriceListItem, number]> => {
    if (!list?.items?.length) return [];
    if (!search.trim()) return list.items.map((it, idx): [ClientPriceListItem, number] => [it, idx]);
    const q = search.trim().toLowerCase();
    return list.items
      .map((it, idx): [ClientPriceListItem, number] => [it, idx])
      .filter(
        ([it]) =>
          (it.description || '').toLowerCase().includes(q) ||
          (it.category || '').toLowerCase().includes(q) ||
          (it.sub_category || '').toLowerCase().includes(q)
      );
  }, [list?.items, search]);

  const handleExport = async () => {
    if (!list?._id) return;
    setExporting(true);
    try {
      await downloadClientPriceListExport(list._id, list.name);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  // Section A (A6) — convert this price list to a ConfirmedOrder
  const handleConvert = async () => {
    if (!list?._id) return;
    if (!window.confirm('Convert this price list to a Confirmed Order? Only items with Qty > 0 will be included.')) return;
    setConverting(true);
    setConvertResult(null);
    try {
      const res = await convertPriceListToConfirmedOrder(list._id);
      const coId = res?.data?.confirmedOrderId;
      setConvertResult({ ok: true, msg: `Confirmed order created: ${coId}` });
      await fetchList();
    } catch (e: any) {
      setConvertResult({ ok: false, msg: e?.message || 'Conversion failed' });
    } finally {
      setConverting(false);
    }
  };

  // Section A — copy the public response URL to clipboard for sharing with the client.
  // Sharing this way should count as "sent" the same as emailing it (see markSent).
  const handleCopyResponseLink = async () => {
    if (!list?._id) return;
    // The token comes with the staff details read this page loaded (D116.1).
    if (!list.accessToken) {
      alert('This price list has no client link yet. Reload the page and try again.');
      return;
    }
    const url = getClientPriceListPublicLink(list._id, list.accessToken);
    try {
      await navigator.clipboard.writeText(url);
      alert(`Public response link copied:\n${url}`);
    } catch {
      // fallback
      prompt('Copy this link:', url);
    }
    try {
      await markClientPriceListSent(list._id);
      await fetchList();
    } catch (e) {
      console.error('Failed to update price list status after copying link:', e);
      alert('Link copied, but the price list status could not be updated to Sent.');
    }
  };

  if (!id) {
    return (
      <div className="bg-brand-bg p-6">
        <p className="text-slate-600">Invalid price list.</p>
        <button
          type="button"
          onClick={() => navigate('/client-price-lists')}
          className="mt-4 text-primary-500 font-medium hover:underline"
        >
          Back to Price Lists
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-brand-bg">
        <LoadingState />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="bg-brand-bg p-6">
        <p className="text-slate-600">Price list not found.</p>
        <button
          type="button"
          onClick={() => navigate('/client-price-lists')}
          className="mt-4 text-primary-500 font-medium hover:underline"
        >
          Back to Price Lists
        </button>
      </div>
    );
  }

  const isSent = !!list.sentAt;

  return (
    <div className="w-full max-w-full overflow-x-auto bg-brand-bg" style={{ fontFamily: "'Public Sans', sans-serif" }}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#E4E6E5] bg-white">
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/client-price-lists')}
            className="flex items-center gap-1 text-slate-900 hover:text-primary-500"
            aria-label="Back to price lists"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col gap-1 shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">{list.name}</h1>
              <ModuleInfoButton content={CLIENT_PRICE_LIST_DETAIL_INFO_CONTENT} ariaLabel="Client Price List detail information" />
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Created: {formatDate(list.createdAt, '—')} | Status:{' '}
              <span className={isSent ? 'text-primary-500' : 'text-slate-400'}>
                {isSent ? 'Sent' : 'Not Sent'}
              </span>
            </p>
          </div>
          <div className="flex-1 min-w-[200px] max-w-sm">
            <div className="flex items-center h-9 px-2 border border-[#D7DBD9] rounded-md bg-primary-50 gap-2">
              <FiSearch className="w-5 h-5 text-slate-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search here"
                className="flex-1 min-w-0 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSendModalOpen(true)}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-primary-500 bg-primary-50 text-primary-500 font-semibold text-xs hover:bg-[#D6E8F4]"
          >
            Send
          </button>
          {hasEdits && (
            <button
              type="button"
              onClick={handleSaveEdits}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-primary-500 bg-primary-500 text-white font-semibold text-xs hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          )}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-primary-500 text-white font-semibold text-xs hover:opacity-90 disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          {/* Section A — copy the public client response link */}
          <button
            type="button"
            onClick={handleCopyResponseLink}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-slate-300 bg-white text-slate-700 font-semibold text-xs hover:bg-slate-50"
            title="Copy the public link clients use to fill in quantities"
          >
            Copy client link
          </button>
          {/* Section A (A6) — convert to ConfirmedOrder */}
          {!list?.confirmedOrderId && (
            <button
              type="button"
              onClick={handleConvert}
              disabled={converting}
              className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold text-xs hover:bg-emerald-100 disabled:opacity-50"
              title="Create a Confirmed Order from the items where the client entered a quantity"
            >
              {converting ? 'Converting…' : 'Convert to order'}
            </button>
          )}
        </div>
      </div>

      {convertResult && (
        <div className={`mx-6 mt-3 rounded-md border px-4 py-2 text-xs ${
          convertResult.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'
        }`}>
          {convertResult.msg}
        </div>
      )}

      {/* Items table */}
      <div className="p-6" style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
        <div className="rounded-lg border border-[#D7DBD9] bg-white inline-block min-w-full">
          {/* The item name stays put while the eleven price and remark columns
              scroll (`frozen-cols-*`, index.css). One column: it is already
              first here, and only a leading run can be pinned. */}
          <table
            className="w-full text-sm min-w-[1200px] frozen-cols-1"
            style={{ '--frozen-divider': '#D7DBD9' } as React.CSSProperties}
          >
            <thead>
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Item Name
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Last Updated
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Category
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Sub Category
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Source
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Unit
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Quantity
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Item Price (US$)
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Markup
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Extension Price
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Price with VAT
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-r border-[#D7DBD9]">
                  Remarks
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 bg-[#F9FAF9] border-b border-[#D7DBD9]">
                  Internal Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItemsWithIndex.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-3 py-8 text-center text-slate-600">
                    No items match your search.
                  </td>
                </tr>
              ) : (
                filteredItemsWithIndex.map(([item, origIdx]) => {
                  const price = typeof item.unit_price === 'number' ? item.unit_price : 0;
                  const qty = editedItems[origIdx]?.quantity ?? (typeof item.quantity === 'number' && item.quantity >= 0 ? item.quantity : 0);
                  const extensionPrice = price * qty;
                  // unit_price already has VAT baked in when the item was added to the
                  // price list (see masterCatalogue.controller's createPriceListFromCatalogue),
                  // so the extension total is the final price — no second VAT multiplication.
                  const withVat = extensionPrice;
                  const lastUpdated = item.last_updated
                    ? formatDateTime(String(item.last_updated), '—')
                    : '—';
                  return (
                    <tr key={origIdx} className="border-b border-[#D7DBD9]/60 last:border-b-0">
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.description || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {lastUpdated}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.category || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.sub_category || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.source || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.packaging || '—'}
                      </td>
                      <td className="px-3 py-2.5 bg-white border-r border-[#D7DBD9]">
                        <input
                          type="number"
                          min={0}
                          value={qty}
                          onChange={(e) => handleEditChange(origIdx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-16 px-2 py-1 text-right border border-[#D7DBD9] rounded text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-900 bg-white border-r border-[#D7DBD9]">
                        ${price.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-900 bg-white border-r border-[#D7DBD9]">
                        {item.markup != null ? `${item.markup}%` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-900 bg-white border-r border-[#D7DBD9]">
                        ${extensionPrice.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-900 bg-white border-r border-[#D7DBD9]">
                        ${withVat.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 bg-white border-r border-[#D7DBD9]">
                        <input
                          type="text"
                          value={editedItems[origIdx]?.remarks ?? item.remarks ?? ''}
                          onChange={(e) => handleEditChange(origIdx, 'remarks', e.target.value)}
                          placeholder="—"
                          className="w-full min-w-[80px] px-2 py-1 border border-[#D7DBD9] rounded text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2.5 bg-white border-[#D7DBD9]">
                        <input
                          type="text"
                          value={editedItems[origIdx]?.internal_remarks ?? item.internal_remarks ?? ''}
                          onChange={(e) => handleEditChange(origIdx, 'internal_remarks', e.target.value)}
                          placeholder="—"
                          className="w-full min-w-[80px] px-2 py-1 border border-[#D7DBD9] rounded text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ClientPriceListEmailDialog
        open={sendModalOpen}
        list={list}
        onClose={() => setSendModalOpen(false)}
        onSent={fetchList}
        onWentNotRecorded={fetchList}
      />
    </div>
  );
}
